/**
 * Chart Group Configuration
 * 
 * Organize charts into groups with custom headings and flexible layouts.
 * Each group can contain multiple charts displayed together with responsive sizing.
 * 
 * ## Flexible Grid Layout System
 * 
 * The configuration supports custom grid sizing at both the group and individual chart levels:
 * 
 * ### Group-Level Grid Sizing (groupGridSize)
 * - Controls how much horizontal space the entire group occupies
 * - Example: `groupGridSize: { xs: 12, md: 6 }` makes group take half width on desktop
 * - Use case: Display multiple groups side-by-side (e.g., Accessibility & Capacity)
 * - Default: Full width if not specified
 * 
 * ### Chart-Level Grid Sizing (gridSize)
 * - Controls individual chart sizing within a group
 * - Example: `gridSize: { xs: 12, md: 4 }` for 3-column layout on desktop
 * - Default: Uses getChartGridSize() calculation if not specified
 * 
 * ### Responsive Breakpoints
 * - xs: 0px+ (mobile)
 * - sm: 600px+ (tablet)
 * - md: 960px+ (small desktop)
 * - lg: 1280px+ (large desktop)
 * - xl: 1536px+ (extra large desktop)
 * 
 * ### Example Usage
 * ```typescript
 * const chartGroups: ChartGroupConfig[] = [
 *   {
 *     groupTitle: "Side-by-Side Group 1",
 *     groupGridSize: { xs: 12, md: 6 },  // Half width on desktop
 *     charts: [...]
 *   },
 *   {
 *     groupTitle: "Side-by-Side Group 2",
 *     groupGridSize: { xs: 12, md: 6 },  // Half width on desktop
 *     charts: [...]
 *   },
 *   {
 *     groupTitle: "Full Width Group",
 *     // No groupGridSize = full width
 *     charts: [...]
 *   }
 * ];
 * ```
 */

import { GridSize } from '@mui/material';

export interface ChartGridSize {
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
}

export interface ChartGroupConfig {
  groupTitle: string;
  groupGridSize?: ChartGridSize;  // Optional: custom grid sizing for the group container
  charts: Array<{
    component: React.ComponentType<any>;
    fullscreenKey: string;
    gridSize?: ChartGridSize;  // Optional: custom grid sizing for individual charts
  }>;
}

export interface TabChartGroups {
  [tabName: string]: ChartGroupConfig[];
}

/**
 * Calculate grid sizes based on layout mode and chart position
 * 
 * @param chartLayout - 'horizontal' or 'vertical' layout mode
 * @param totalCharts - Total number of charts in the group
 * @param chartIndex - Index of the current chart (0-based)
 * @returns Grid size object for Material-UI Grid component
 * 
 * Layout Logic:
 * - Vertical: All charts take full width (12 columns)
 * - Horizontal: Charts are arranged in 2-column grid
 *   - Even number of charts: All charts take 6 columns (2 per row)
 *   - Odd number of charts: Last chart takes full width (12 columns)
 * 
 * Examples:
 * - 2 charts horizontal: [6, 6]
 * - 3 charts horizontal: [6, 6, 12] (first two side-by-side, third full width)
 * - 4 charts horizontal: [6, 6, 6, 6] (all in 2 columns)
 * - Any vertical: [12, 12, 12, ...] (all full width)
 */
export const getChartGridSize = (
  chartLayout: 'vertical' | 'horizontal',
  totalCharts: number,
  chartIndex: number
): ChartGridSize => {
  // Vertical mode: all charts full width
  if (chartLayout === 'vertical') {
    return { xs: 12, md: 12 };
  }
  
  // Horizontal mode: 2-column layout
  const isLastChart = chartIndex === totalCharts - 1;
  const isOddTotal = totalCharts % 2 === 1;
  
  // If last chart and odd total, make it full width
  if (isLastChart && isOddTotal) {
    return { xs: 12, md: 12 };
  }
  
  // Otherwise, half width for side-by-side layout
  return { xs: 12, md: 6 };
};

/**
 * Define chart groups for each tab
 * 
 * Example:
 * 'Site E': [
 *   {
 *     groupTitle: 'LTE Cell KPI',
 *     charts: [
 *       { component: ErabEstablishmentEcharts, fullscreenKey: 'erabE' },
 *       { component: VolteEstablishmentEcharts, fullscreenKey: 'volteE' },
 *       { component: RrcConnectionEstablishmentEcharts, fullscreenKey: 'rrcE' }
 *     ]
 *   },
 *   {
 *     groupTitle: 'Network Performance',
 *     charts: [
 *       { component: ThroughputChart, fullscreenKey: 'throughput' },
 *       { component: LatencyChart, fullscreenKey: 'latency' }
 *     ]
 *   }
 * ]
 * 
 * Custom Grid Sizing:
 * You can override automatic sizing by providing gridSize:
 * {
 *   component: CustomChart,
 *   fullscreenKey: 'custom',
 *   gridSize: { xs: 12, md: 4 }  // Force 1/3 width on medium screens
 * }
 */
export const tabChartGroups: TabChartGroups = {
  'Site E': [
    {
      groupTitle: 'LTE Cell KPI',
      charts: [
        // Charts will be imported and added in Performance.tsx
        // This is just the configuration structure
      ]
    }
    // Add more groups here as needed:
    // {
    //   groupTitle: 'Network Performance',
    //   charts: [...]
    // },
    // {
    //   groupTitle: 'Resource Utilization',
    //   charts: [...]
    // }
  ],
  
  'Site P': [
    {
      groupTitle: 'Establishment Metrics',
      charts: []
    }
  ],
  
  'ResourcesP': [
    {
      groupTitle: 'System Resources',
      charts: []
    }
  ]
};
